import React from "react";
import {
  Box,
  Grid,
  SpaceBetween,
  ExpandableSection,
  ColumnLayout,
  Container,
} from "@amzn/awsui-components-react";
import { TEMPLATE_APP } from "../Constants";
import CustomMultiSelect from "./CustomMultiSelect";
import CustomMultiInput from "./CustomMultiInput";
import CustomSelect from "./CustomSelect";
import CustomInput from "./CustomInput";

const OtherDetailsTile = ({
  isEditable,
  getValue,
  updateValue,
  getSelectFieldValues,
  getMultiSelectedOptions,
  getMultiInputFieldValues,
  shouldShowMandatory,
  renderJsonEdited,
  usecaseLedgerImpact,
  usecaseRegions
}) => {
  const renderMandatoryIndicator = () => (
    <Box color="text-status-error" fontSize="heading-l" fontWeight="bold">
      *
    </Box>
  );

  const renderLedgerImpactSection = () => (
    <ExpandableSection
      defaultExpanded={true}
      headerText="Ledger Impact"
      variant="borderless"
    >
      {isEditable(TEMPLATE_APP.LEDGER_INFO) ? (
        <Grid gridDefinition={[{ colspan: 11 }, { colspan: 1 }]}>
          <CustomMultiSelect 
            field={TEMPLATE_APP.LEDGER_INFO}
            updateValue={updateValue} 
            getValues={getSelectFieldValues} 
            getSelectedOptions={getMultiSelectedOptions} 
          />
          {shouldShowMandatory() && renderMandatoryIndicator()}
        </Grid>
      ) : (
        <SpaceBetween size="xs">
          {usecaseLedgerImpact?.map((item) => (
            <Box key={item} variant="p">
              {item}
            </Box>
          ))}
        </SpaceBetween>
      )}
    </ExpandableSection>
  );

  const renderUseCaseRegionsSection = () => (
    <ExpandableSection
      defaultExpanded={true}
      headerText="Use Case Regions"
      variant="borderless"
    >
      {isEditable(TEMPLATE_APP.REGION) ? (
        <CustomMultiSelect 
          field={TEMPLATE_APP.REGION}
          updateValue={updateValue} 
          getValues={getSelectFieldValues} 
          getSelectedOptions={getMultiSelectedOptions} 
        />
      ) : (
        <SpaceBetween size="xs">
          {usecaseRegions?.map((item) => (
            <Box key={item} variant="p">
              {item}
            </Box>
          ))}
        </SpaceBetween>
      )}
    </ExpandableSection>
  );

  const renderLaunchMCMSection = () => (
    <ExpandableSection
      defaultExpanded={true}
      headerText="Launch MCM"
      variant="borderless"
    >
      {isEditable(TEMPLATE_APP.LAUNCH_MCM) ? (
        <Grid gridDefinition={[{ colspan: 11 }, { colspan: 1 }]}>
          <CustomMultiInput 
            field={TEMPLATE_APP.LAUNCH_MCM} 
            updateValue={updateValue} 
            getValues={getMultiInputFieldValues}
          />
          {shouldShowMandatory() && renderMandatoryIndicator()}
        </Grid>
      ) : (
        <SpaceBetween size="xs">
          {getMultiInputFieldValues(TEMPLATE_APP.LAUNCH_MCM) && getMultiInputFieldValues(TEMPLATE_APP.LAUNCH_MCM).length > 0 ? (
            getMultiInputFieldValues(TEMPLATE_APP.LAUNCH_MCM).map((item) => (
              <Box key={item} variant="p">
                {item.trim()}
              </Box>
            ))
          ) : (
            <Box color="text-body-secondary">
              Not Available
            </Box>
          )}
        </SpaceBetween>
      )}
    </ExpandableSection>
  );

  const renderLaunchDateSection = () => (
    <SpaceBetween size="xs">
      <Box variant="h4">Launch Date</Box>
      {isEditable(TEMPLATE_APP.LAUNCH_QQ_YY) ? (
        <Grid gridDefinition={[{ colspan: 11 }, { colspan: 1 }]}>
          <CustomSelect 
            field={TEMPLATE_APP.LAUNCH_QQ_YY} 
            getValue={getValue} 
            updateValue={updateValue} 
            getOptions={getSelectFieldValues} 
          />
          {renderJsonEdited[TEMPLATE_APP.STATUS] === 'Active' && renderMandatoryIndicator()}
        </Grid>
      ) : (
        <Box variant="p">
          {getValue(TEMPLATE_APP.LAUNCH_QQ_YY) || 
           <Box color="text-body-secondary">Not specified</Box>
          }
        </Box>
      )}
    </SpaceBetween>
  );

  const renderAnnualMERSavingsSection = () => (
    <SpaceBetween size="xs">
      <Box variant="h4">Annual MER Savings</Box>
      {isEditable(TEMPLATE_APP.ANNUAL_MER_SAVINGS) ? (
        <Grid gridDefinition={[{ colspan: 11 }, { colspan: 1 }]}>
          <CustomInput 
            field={TEMPLATE_APP.ANNUAL_MER_SAVINGS} 
            placeholder="Annual MER savings" 
            updateValue={updateValue} 
            getValue={getValue}
          />
          {shouldShowMandatory() && renderMandatoryIndicator()}
        </Grid>
      ) : (
        <Box variant="p">
          {getValue(TEMPLATE_APP.ANNUAL_MER_SAVINGS) || 
           <Box color="text-body-secondary">Not specified</Box>
          }
        </Box>
      )}
    </SpaceBetween>
  );

  const renderMJECountSection = () => (
    <SpaceBetween size="xs">
      <Box variant="h4">MJE Count</Box>
      {isEditable(TEMPLATE_APP.PROCESS_MJE_COUNT) ? (
        <CustomInput 
          field={TEMPLATE_APP.PROCESS_MJE_COUNT} 
          placeholder="MJE count" 
          updateValue={updateValue} 
          getValue={getValue}
        />
      ) : (
        <Box variant="p">
          {getValue(TEMPLATE_APP.PROCESS_MJE_COUNT) || 
           <Box color="text-body-secondary">Not specified</Box>
          }
        </Box>
      )}
    </SpaceBetween>
  );

  const renderFinancialYearImpactSection = () => (
    <SpaceBetween size="xs">
      <Box variant="h4">Financial year impact</Box>
      {isEditable(TEMPLATE_APP.FINANCIAL_YEAR_IMPACT) ? (
        <Grid gridDefinition={[{ colspan: 11 }, { colspan: 1 }]}>
          <CustomSelect 
            field={TEMPLATE_APP.FINANCIAL_YEAR_IMPACT} 
            getValue={getValue} 
            updateValue={updateValue} 
            getOptions={getSelectFieldValues} 
          />
          {shouldShowMandatory() && renderMandatoryIndicator()}
        </Grid>
      ) : (
        <Box variant="p">
          {getValue(TEMPLATE_APP.FINANCIAL_YEAR_IMPACT) || 
           <Box color="text-body-secondary">Not specified</Box>
          }
        </Box>
      )}
    </SpaceBetween>
  );

  return (
    <Box padding={{ left: "l" }}>
      <SpaceBetween size="l">
        <ColumnLayout columns={3}>
        {renderLedgerImpactSection()}
        
        {renderUseCaseRegionsSection()}
        
        {renderLaunchMCMSection()}
      </ColumnLayout>
      
      <ColumnLayout columns={3}>
        {renderLaunchDateSection()}
        {renderAnnualMERSavingsSection()}
        {renderMJECountSection()}
      </ColumnLayout>
      
      <ColumnLayout columns={3}>
        {renderFinancialYearImpactSection()}
      </ColumnLayout>
      </SpaceBetween>
    </Box>
  );
};

export default OtherDetailsTile;
